import { Loader2 } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Spinner + legenda centralizados, para o corpo de uma seção/página inteira
 * enquanto a primeira carga de dados não chegou. Para placeholders que
 * preservam o layout final (tabela, cards), prefira `TableSkeleton`/
 * `CardSkeleton` abaixo — só caem para este spinner genérico quando a forma
 * final do conteúdo ainda não é conhecida.
 */
export function LoadingState({
  label = "Carregando…",
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex flex-col items-center justify-center gap-2.5 py-12 text-fg-mute",
        className,
      )}
    >
      <Loader2 className="size-5 animate-spin text-accent-text" aria-hidden="true" />
      <span className="text-[0.8125rem]">{label}</span>
    </div>
  );
}

/** Placeholder de tabela: cabeçalho fixo + N linhas de barras cinza, nas
 *  mesmas proporções de colunas de uma `DataTable` real (ver DataTable.tsx). */
export function TableSkeleton({
  rows = 5,
  columns = 4,
  className,
}: {
  rows?: number;
  columns?: number;
  className?: string;
}) {
  return (
    <div className={cn("w-full space-y-2.5", className)} role="status" aria-label="Carregando tabela">
      <div className="flex gap-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4">
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton key={c} className="h-8 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Placeholder de card (KPI, painel) — título curto + valor + linha de nota. */
export function CardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("space-y-2.5 rounded-lg border border-border bg-surface p-4", className)}
      role="status"
      aria-label="Carregando"
    >
      <Skeleton className="h-2.5 w-24" />
      <Skeleton className="h-6 w-16" />
      <Skeleton className="h-2.5 w-32" />
    </div>
  );
}
